<template>
    <kiosk-layout formContainer>
        <step-form-heading :icon="faGrid2Plus"> New Group </step-form-heading>

        <form-wrapper
            :sections="[
                { id: 'details', label: 'Details' },
                { id: 'orgUnit', label: 'Organizational Unit' },
                { id: 'attributes', label: 'Custom Attributes' },
            ]"
        >
            <div v-if="prefillTeamId" class="max-w-(--breakpoint-md) w-full mb-2 md:mb-8 mx-auto">
                <vx-alert data-test="prefill-team-alert" color="info" :closeable="false" shadow>
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                    </template>
                    <div class="flex items-center justify-between">
                        <span><strong>Creating in</strong> {{ prefillTeamName }}</span>
                        <vx-button :href="backHref" size="sm" color="info"
                            >Go Back<font-awesome-icon widthAuto :icon="faSquareArrowUpLeft"></font-awesome-icon
                        ></vx-button>
                    </div>
                </vx-alert>
            </div>

            <kiosk-group-form-details id="details" v-model="form" :lockedGroupId="!!prefillGroupId" />

            <kiosk-group-form-org-unit id="orgUnit" v-model="orgUnit" :teamId="form.team_id" />

            <kiosk-group-form-custom-attributes id="attributes" v-model="customAttributes" />

            <vx-sticky-footer>
                <vx-button :href="backHref" class="w-20" size="lg" color="muted"> Cancel </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    class="grow"
                    size="lg"
                    color="primary"
                    :loading="processing"
                >
                    Create Group
                </vx-button>
            </vx-sticky-footer>
        </form-wrapper>
    </kiosk-layout>
</template>

<script setup>
import KioskLayout from '../../KioskLayout.vue';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { VxAlert, VxStickyFooter, VxButton } from '@voxie/frontend-components';
import { ref, computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import KioskGroupFormDetails from './KioskGroupFormDetails.vue';
import KioskGroupFormCustomAttributes from './KioskGroupFormCustomAttributes.vue';
import KioskGroupFormOrgUnit from './KioskGroupFormOrgUnit.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { adminGroupsClient, adminTeamsClient, orgUnitGroupsClient } from '~/services';
import { faSquareArrowUpLeft, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { useUrlSearchParams } from '@vueuse/core';

const queryParams = useUrlSearchParams();

const defaultData = () => ({
    group_id: '',
    team_id: null,
    name: '',
    time_zone: null,
    hubspot_company_id: null,
});
const form = ref(defaultData());

const prefillTeamId = queryParams.team_id ? Number(queryParams.team_id) : null;
const prefillTeamName = ref(prefillTeamId ? String(prefillTeamId) : '');
const prefillGroupId = ref(queryParams.setup_group_id ? String(queryParams.setup_group_id) : null);

if (prefillGroupId.value) {
    form.value.group_id = prefillGroupId.value;
}

if (prefillTeamId) {
    form.value.team_id = prefillTeamId;

    adminTeamsClient
        .getTeam(prefillTeamId)
        .then((response) => {
            prefillTeamName.value = response.data.name;
        })
        .catch(() => {});
}

const backHref = computed(() => (prefillTeamId ? `/kiosk/teams/${prefillTeamId}/groups` : '/kiosk/groups'));

const customAttributes = ref([]);
const orgUnit = ref(null);

const save = async () => {
    const groupData = {
        group_id: form.value.group_id,
        name: form.value.name,
        time_zone: form.value.time_zone,
        hubspot_company_id: form.value.hubspot_company_id,
        custom_attributes: customAttributes.value,
    };

    if (orgUnit.value?.id) {
        return orgUnitGroupsClient.createOrgUnitGroup(form.value.team_id, orgUnit.value.id, groupData);
    }

    return adminGroupsClient.createGroup(form.value.team_id, groupData);
};

const { submit, processing } = useFormSubmission({
    data: form,
    submit: save,
    preserveScroll: false,
    reset: () => {
        // if update functionality is added to this form, the data should not be cleared
        form.value = defaultData();
        if (prefillTeamId) {
            form.value.team_id = prefillTeamId;
        }
        queryParams.setup_group_id = undefined;
        prefillGroupId.value = null;
        customAttributes.value = [];
        orgUnit.value = null;
    },
    notification: () => ({
        message: prefillTeamName.value ? `Group created in ${prefillTeamName.value}` : 'Group created',
        action: {
            icon: faSquareArrowUpLeft,
            label: prefillTeamId ? 'Back to Team Groups' : 'Back to Groups',
            href: backHref.value,
        },
    }),
});
</script>
